"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Package, ShoppingCart, Users, Settings, ChevronLeft, ChevronRight,
  Home, Tag, Truck, MessageSquare, Palette, HelpCircle, CreditCard,
  Bell, BadgePercent, Phone, Receipt, Sparkles, LayoutGrid, Grid3X3,
  MapPin, Map, Building2, Building, IndianRupee, List, Image, Images,
  GalleryHorizontal, FileText, Star, Percent, Ticket, Navigation,
  Ruler, Database, Box, Layers, FlaskConical, CircleDollarSign,
  ClipboardList, AlertCircle, Wand2, LogOut, ShoppingBag,
  ReceiptIndianRupee, BarChart3, TrendingUp, PieChart, LineChart, Calendar,
} from "lucide-react";
import LogoutButton from "./LogoutButton";
import { useSettings } from "./context/SettingsContext";
import { useAppContext } from "../App";
import { CDN_URL } from "./config/apiconfig";

// ─── Menu config ──────────────────────────────────────────────────────────────
const MENU_ITEMS = [
  {
    section: "Main",
    items: [
      { id: "dashboard", label: "Dashboard", icon: Home, path: "/dashboard" },
    ],
  },
  {
    section: "Masters",
    items: [
      {
        id: "masters",
        label: "Masters",
        icon: Database,
        children: [
          { id: "categories",    label: "Categories",     icon: LayoutGrid,  path: "/categories" },
          { id: "subcategories", label: "Sub-Categories", icon: Grid3X3,     path: "/subcategories" },
          { id: "modals",        label: "Models",         icon: Box,         path: "/modals" },
          { id: "producttype",   label: "Product Types",  icon: Tag,         path: "/producttype" },
          { id: "products",      label: "Products",       icon: Package,     path: "/products" },
          {
            id: "product-attributes",
            label: "Product Attributes",
            icon: Palette,
            children: [
              { id: "materials", label: "Materials", icon: FlaskConical, path: "/product-attributes/materials" },
              { id: "colours",   label: "Colours",   icon: Palette,      path: "/product-attributes/colours" },
              { id: "shapes",    label: "Shapes",    icon: Sparkles,     path: "/product-attributes/shapes" },
              { id: "Sizes",     label: "Sizes",     icon: Ruler,        path: "/product-attributes/Sizes" },
            ],
          },
          {
            id: "media-management",
            label: "Media",
            icon: Images,
            children: [
              { id: "banners",              label: "Banners",   icon: Image, path: "/product-attributes/Banners" },
              { id: "customization-images", label: "Customize", icon: Wand2, path: "/product-attributes/customization-images" },
            ],
          },
          { id: "suppliers",         label: "Suppliers", icon: Truck,            path: "/suppliers" },
          { id: "states",            label: "States",    icon: MapPin,           path: "/states" },
          { id: "districts",         label: "Districts", icon: Map,              path: "/districts" },
          { id: "mandals",           label: "Mandals",   icon: Navigation,       path: "/mandals" },
          { id: "cities",            label: "Cities",    icon: Building2,        path: "/cities" },
          { id: "expense",           label: "Expense",   icon: Receipt,          path: "/expenses" },
          { id: "items",             label: "Items",     icon: List,             path: "/items" },
          { id: "currency-settings", label: "Currency",  icon: CircleDollarSign, path: "/currency-settings" },
        ],
      },
    ],
  },
  {
    section: "Commerce",
    items: [
      { id: "order-types",  label: "Order Types",  icon: ClipboardList, path: "/order-types" },
      { id: "orders",       label: "Orders",       icon: ShoppingCart,  path: "/orders" },
      { id: "transactions", label: "Transactions", icon: CreditCard,    path: "/transactions" },
      {
        id: "customers",
        label: "Customers",
        icon: Users,
        children: [
          { id: "customer-dashboard", label: "Overview",      icon: Home,  path: "/customers-coming-soon" },
          { id: "customers-list",     label: "All Customers", icon: Users, path: "/customers" },
        ],
      },
      {
        id: "vouchers",
        label: "Vouchers",
        icon: FileText,
        children: [
          { id: "expense-voucher",  label: "Expense Voucher",  icon: Receipt,    path: "/expensevouchers" },
          { id: "purchase-voucher", label: "Purchase Voucher", icon: ShoppingBag, path: "/purchase-voucher" },
          { id: "payment-voucher",  label: "Payment Voucher",  icon: CreditCard, path: "/payment-voucher" },
        ],
      },
    ],
  },
  {
  section: "Reports",
  items: [
    {
      id: "business-reports",
      label: "Business Reports",
      icon: BarChart3,
      path: "/business-reports",
    },
  ],
},
  {
    section: "Marketing",
    items: [
      { id: "reviews",     label: "Reviews",              icon: Star,          path: "/reviews" },
      { id: "faqs",        label: "FAQs",                 icon: HelpCircle,    path: "/faqs" },
      { id: "issue-types", label: "Issue Types",          icon: AlertCircle,   path: "/issue-types" },
      { id: "issues",      label: "Issues & Suggestions", icon: MessageSquare, path: "/issues" },
      { id: "discount",    label: "Discount Types",       icon: Percent,       path: "/discount" },
      { id: "offers",      label: "Coupons",              icon: Ticket,        path: "/coupons" },
    ],
  },
  {
    section: "System",
    items: [
      { id: "Notifications", label: "Notifications", icon: Bell,               path: "/notification" },
      { id: "contact",       label: "Contact",        icon: Phone,              path: "/contact" },
      { id: "tax",           label: "Tax",            icon: ReceiptIndianRupee, path: "/tax" },
      { id: "settings",      label: "Settings",       icon: Settings,           path: "/settings" },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const flatItems = MENU_ITEMS.flatMap(s => s.items);

const isChildActive = (children = [], pathname) =>
  children.some(child =>
    child.path ? pathname === child.path : isChildActive(child.children || [], pathname)
  );

const findActiveAncestors = (items, pathname, acc = new Set()) => {
  for (const item of items) {
    if (item.children && isChildActive(item.children, pathname)) {
      acc.add(item.id);
      findActiveAncestors(item.children, pathname, acc);
    }
  }
  return acc;
};

// ─── FlyoutPanel ─────────────────────────────────────────────────────────────
// Renders outside the sidebar so pointer events always work.
// Parent keeps it alive while EITHER the trigger OR this panel is hovered.
const FlyoutPanel = ({ item, anchorY, onClose, onMouseEnter, onMouseLeave, location }) => {
  const [openSubs, setOpenSubs] = useState(new Set());

  useEffect(() => {
    if (item?.children) {
      setOpenSubs(findActiveAncestors(item.children, location.pathname));
    }
  }, [item, location.pathname]);

  const toggleSub = id =>
    setOpenSubs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Clamp so panel never overflows bottom of screen
  const top = Math.min(anchorY - 8, window.innerHeight - 480);

  const renderItems = (children, depth = 0) =>
    children.map(child => {
      if (child.children) {
        const isOpen = openSubs.has(child.id);
        const active = isChildActive(child.children, location.pathname);
        const CIcon  = child.icon;
        return (
          <div key={child.id}>
            <button
              onClick={() => toggleSub(child.id)}
              className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-xs transition-colors
                ${active ? "text-teal-700 font-semibold" : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"}`}
              style={{ paddingLeft: depth * 12 + 8 }}
            >
              {CIcon && <CIcon className="w-3 h-3 flex-shrink-0 opacity-70" />}
              <span className="flex-1 text-left">{child.label}</span>
              <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`} />
            </button>
            {isOpen && (
              <div
                className="border-l-2 border-orange-200 space-y-0.5 mt-0.5"
                style={{ marginLeft: depth * 12 + 16 }}
              >
                {renderItems(child.children, depth + 1)}
              </div>
            )}
          </div>
        );
      }

      const CIcon  = child.icon;
      const active = location.pathname === child.path;
      return (
        <Link
          key={child.id}
          to={child.path}
          onClick={onClose}
          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors
            ${active
              ? "bg-teal-600 text-white"
              : "text-gray-600 hover:bg-teal-50 hover:text-teal-700"}`}
          style={{ paddingLeft: depth * 12 + 8 }}
        >
          {CIcon && <CIcon className="w-3 h-3 flex-shrink-0" />}
          <span className="flex-1">{child.label}</span>
          {active && <span className="w-1.5 h-1.5 rounded-full bg-teal-300 flex-shrink-0" />}
        </Link>
      );
    });

  return (
    <div
      className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl w-56 overflow-hidden"
      style={{ top: Math.max(8, top), left: 76 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-teal-50 border-b border-teal-100">
        <item.icon className="w-4 h-4 text-teal-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-teal-800">{item.label}</span>
      </div>
      {/* Scrollable children */}
      <div className="px-2 py-2 space-y-0.5 overflow-y-auto max-h-[65vh]">
        {renderItems(item.children)}
      </div>
    </div>
  );
};

// ─── DashboardSidebar ─────────────────────────────────────────────────────────
const DashboardSidebar = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { adminProfile: profile } = useSettings();
  const { isCollapsed, setIsCollapsed, isMobileMenuOpen, setIsMobileMenuOpen } = useAppContext();

  const [openMenus, setOpenMenus] = useState(new Set());
  const [tooltip,   setTooltip]   = useState({ label: "", y: 0, visible: false });
  const [flyout,    setFlyout]    = useState(null); // { item, y } | null
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false
  );

  // Shared close-timer ref — cleared whenever either zone is entered
  const closeTimer = useRef(null);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setFlyout(null), 120);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => {
    const handle = () => setIsDesktop(window.innerWidth >= 1024);
    handle();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => {
    setOpenMenus(findActiveAncestors(flatItems, location.pathname));
  }, [location.pathname]);

  // Close flyout & tooltip on navigation
  useEffect(() => {
    setFlyout(null);
    setTooltip(t => ({ ...t, visible: false }));
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open. This stops the page
  // behind it from scrolling, which is what causes "fixed" elements to
  // drift/shrink relative to the visible viewport on mobile browsers
  // (especially iOS Safari) when the page content is tall — the exact
  // symptom of the sidebar rendering as a small floating box instead of
  // a full-height panel.
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileMenuOpen]);

  const collapsed = isCollapsed && isDesktop;

  const toggleMenu = id =>
    setOpenMenus(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    setFlyout(null);
    setTooltip(t => ({ ...t, visible: false }));
  };

  const showTooltip = (e, label) => {
    if (!collapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ label, y: rect.top + rect.height / 2, visible: true });
  };
  const hideTooltip = () => setTooltip(t => ({ ...t, visible: false }));

  const adminName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
    : "Admin";
  const initials =
    adminName.split(" ").filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "A";
  const adminRole = localStorage.getItem("role") || "admin";

  const leafCls = active =>
    active
      ? "bg-teal-600 text-white shadow-sm"
      : "text-gray-600 hover:bg-gray-50 hover:text-gray-800";

  const parentCls = active =>
    active
      ? "bg-teal-50 text-teal-700 font-semibold"
      : "text-gray-700 hover:bg-gray-50";

  // Recursive renderer for expanded sidebar
  const renderChildren = (children, depth = 1) => {
    const pl = depth * 16 + 8;
    return children.map(child => {
      if (child.children) {
        const isOpen = openMenus.has(child.id);
        const active = isChildActive(child.children, location.pathname);
        const CIcon  = child.icon;
        return (
          <div key={child.id}>
            <button
              onClick={() => toggleMenu(child.id)}
              style={{ paddingLeft: pl }}
              className={`flex items-center gap-2.5 w-full pr-3 py-2 rounded-lg text-sm transition-colors ${parentCls(active)}`}
            >
              {CIcon && <CIcon className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />}
              <span className="flex-1 text-left">{child.label}</span>
              <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
            </button>
            {isOpen && (
              <div
                className="mt-0.5 mb-0.5"
                style={{  borderLeft: "2px solid #fed7aa", marginLeft: pl + 4 }}
              >
                {renderChildren(child.children, depth + 1)}
              </div>
            )}
          </div>
        );
      }
      const CIcon  = child.icon;
      const active = location.pathname === child.path;
      return (
        <Link
          key={child.id}
          to={child.path}
          onClick={handleNavClick}
          style={{ paddingLeft: pl }}
          className={`flex items-center gap-2.5 pr-3 py-2 rounded-lg text-sm transition-colors ${leafCls(active)}`}
        >
          {CIcon && <CIcon className="w-3.5 h-3.5 flex-shrink-0" />}
          <span>{child.label}</span>
          {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-300" />}
        </Link>
      );
    });
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside className={`
        bg-white border-r border-gray-200
        transition-all duration-300 ease-in-out flex flex-col h-screen fixed left-0 top-0 z-40 overflow-hidden
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        ${collapsed ? "lg:w-[70px]" : "lg:w-64"} w-64 shadow-sm
      `}>

        {/* ── Brand header ── */}
        <div className={`flex-shrink-0 border-b border-gray-100 ${collapsed ? "px-0 py-4" : "px-5 py-4"}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl flex items-center justify-center shadow-sm">
                <img
                  src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
                  alt="M"
                  className="w-7 h-7 object-contain"
                  onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                />
                <span style={{ display: "none" }} className="text-white text-lg font-bold">M</span>
              </div>
              <button
                onClick={() => setIsCollapsed(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors hidden lg:flex items-center justify-center"
              >
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/dashboard")}>
                <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                  <img
                    src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
                    alt="M"
                    className="w-7 h-7 object-contain"
                    onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                  />
                  <span style={{ display: "none" }} className="text-white text-lg font-bold">M</span>
                </div>
                <div>
                  <h2 className="text-base font-bold text-teal-800 leading-tight">ManaPrint</h2>
                  <p className="text-[11px] text-gray-400 leading-tight">Admin Panel</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsCollapsed(true)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors hidden lg:flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-500" />
                </button>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors lg:hidden flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5 scrollbar-thin scrollbar-thumb-orange-100">
          {MENU_ITEMS.map((section, si) => (
            <div key={section.section} className={si > 0 ? "mt-2" : ""}>
              {!collapsed && (
                <p className="px-2 pb-1 pt-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {si > 0 ? section.section : ""}
                </p>
              )}
              {collapsed && si > 0 && (
                <div className="mx-auto w-6 border-t border-gray-200 my-2" />
              )}

              {section.items.map(item => {
                const Icon   = item.icon;
                const active = item.path
                  ? location.pathname === item.path
                  : isChildActive(item.children || [], location.pathname);
                const isOpen = openMenus.has(item.id);

                if (item.children) {
                  return (
                    <div
                      key={item.id}
                      // ── hover zone on the trigger ──
                      onMouseEnter={e => {
                        if (!collapsed) return;
                        cancelClose();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setFlyout({ item, y: rect.top });
                        setTooltip(t => ({ ...t, visible: false }));
                      }}
                      onMouseLeave={() => {
                        if (!collapsed) return;
                        scheduleClose();
                      }}
                    >
                      <button
                        onClick={() => {
                          if (!collapsed) toggleMenu(item.id);
                          // In collapsed mode clicking keeps flyout via hover;
                          // nothing extra needed.
                        }}
                        className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-colors text-sm
                          ${collapsed ? "justify-center" : ""}
                          ${active ? "bg-teal-50 text-teal-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"}`}
                      >
                        <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors
                          ${active ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-500"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left font-medium">{item.label}</span>
                            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                          </>
                        )}
                      </button>

                      {isOpen && !collapsed && (
                        <div className="ml-3 mt-0.5 mb-1 pl-3 border-l-2 border-gray-200 space-y-0.5">
                          {renderChildren(item.children, 1)}
                        </div>
                      )}
                    </div>
                  );
                }

                // Leaf item
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={handleNavClick}
                    onMouseEnter={e => showTooltip(e, item.label)}
                    onMouseLeave={hideTooltip}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm
                      ${collapsed ? "justify-center" : ""}
                      ${active
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-200"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"}`}
                  >
                    <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors
                      ${active ? "bg-white/20" : "bg-gray-100 text-gray-500"}`}>
                      <Icon className={`w-4 h-4 ${active ? "text-white" : ""}`} />
                    </div>
                    {!collapsed && (
                      <>
                        <span className="flex-1 font-medium">{item.label}</span>
                        {active && <span className="w-1.5 h-1.5 rounded-full bg-white/60" />}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── Footer profile ── */}
        <div className="flex-shrink-0 border-t border-gray-100 p-3">
          {!collapsed ? (
            <div className="flex items-center gap-3 px-2 py-2 rounded-xl bg-teal-50/60 border border-teal-100">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center flex-shrink-0">
                {profile?.image ? (
                  <img src={`${CDN_URL}/${profile.image}`} alt="Profile" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = "none"; }} />
                ) : (
                  <span className="text-white text-xs font-bold">{initials}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">{adminName}</p>
                <p className="text-[10px] text-gray-500 truncate capitalize">{adminRole}</p>
              </div>
              <LogoutButton isCollapsed={false} iconOnly />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                {profile?.image ? (
                  <img src={`${CDN_URL}/${profile.image}`} alt="Profile" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = "none"; }} />
                ) : (
                  <span className="text-white text-xs font-bold">{initials}</span>
                )}
              </div>
              <LogoutButton isCollapsed={true} />
            </div>
          )}
        </div>
      </aside>

      {/* ── Tooltip (leaf items only) ── */}
      {tooltip.visible && collapsed && (
        <div
          className="fixed z-[9999] px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg shadow-xl whitespace-nowrap pointer-events-none -translate-y-1/2"
          style={{ top: tooltip.y, left: 78 }}
        >
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
          {tooltip.label}
        </div>
      )}

      {/* ── Flyout panel (parent items, collapsed mode) ── */}
      {flyout && collapsed && (
        <FlyoutPanel
          item={flyout.item}
          anchorY={flyout.y}
          location={location}
          onClose={handleNavClick}
          onMouseEnter={cancelClose}   // ← entering panel cancels the close timer
          onMouseLeave={scheduleClose} // ← leaving panel starts the close timer
        />
      )}
    </>
  );
};

export default DashboardSidebar;